import React, { useState, useMemo } from 'react'
import { Stack, Text, Card, Stat, Select, SimpleTabs } from '@/components/lib'
import utils from '../../utils/utils'

function formatDurationBlock (block) {
  if (!block || !block.n) return '-'
  return `${block.median}d median (${block.min}-${block.max}d, n=${block.n} participants)`
}

function formatMessages (roll, windowKey) {
  const m = roll?.messages?.[windowKey]
  if (!m) return '-'
  return `${m.fromParticipant} in / ${m.fromCoordinator} out (${m.total} total)`
}

function RollupDetail ({ roll }) {
  if (!roll || roll.participantCount === 0) {
    return <Text fontSize='sm' color='fg.subtle'>No allocated participants in this view.</Text>
  }
  const pr = roll.progress || {}
  const pred = roll.prediction

  return (
    <Stack gap={3} fontSize='sm'>
      <Stack direction='row' flexWrap='wrap' gap={4} minW='350px'>
        <Stat label='Completed' value={`${pr.completed} (${Math.round(pr.completedPct)}%)`} />
        <Stat label='Started' value={`${pr.started} (${Math.round(pr.startedPct)}%)`} />
        <Stat label='≥25 / 50 / 75%' value={`${pr.atLeast25Pct} / ${pr.atLeast50Pct} / ${pr.atLeast75Pct}`} />
      </Stack>
      <Stack gap={1}>
        <Text>Behind schedule: {roll.behindSchedule?.count ?? 0} ({roll.behindSchedule?.pct ?? 0}%)</Text>
        <Text>Stalled ({roll.stalled?.afterDays ?? 14}d+ no response): {roll.stalled?.count ?? 0} ({roll.stalled?.pct ?? 0}%)</Text>
      </Stack>
      <Stack gap={1}>
        <Text fontWeight='bold' color='fg.subtle'>All participants</Text>
        <Text fontWeight='medium' color='fg.subtle' fontSize='xs'>First → latest response (≥2 responses)</Text>
        <Text>{formatDurationBlock(roll.responseSpanDays)}</Text>
        <Text fontWeight='medium' color='fg.subtle' fontSize='xs' mt={2}>Enrollment → latest response</Text>
        <Text>{formatDurationBlock(roll.enrollmentToLastResponseDays)}</Text>
      </Stack>
      <Stack gap={1}>
        <Text fontWeight='bold' color='fg.subtle'>Fully completed participants</Text>
        <Text fontWeight='medium' color='fg.subtle' fontSize='xs'>First → last response</Text>
        <Text>{formatDurationBlock(roll.completedParticipantsResponseSpanDays)}</Text>
        <Text fontWeight='medium' color='fg.subtle' fontSize='xs' mt={2}>Enrollment → last response</Text>
        <Text>{formatDurationBlock(roll.completedParticipantsEnrollmentToLastDays)}</Text>
      </Stack>
      {pred && pred.nCompleters > 0 && (
        <Stack gap={1}>
          <Text fontWeight='medium'>Predicted end if cohort enrolled today</Text>
          <Text color='fg.subtle'>
            From {pred.nCompleters} completers: median {pred.medianDaysEnrollmentToLastResponse}d →{' '}
            {utils.formatDateTime(pred.predictedEndAtMedian)}
            {' · '}mean {pred.meanDaysEnrollmentToLastResponse}d → {utils.formatDateTime(pred.predictedEndAtMean)}
          </Text>
        </Stack>
      )}
      <Stack gap={1}>
        <Text fontWeight='bold' color='fg.subtle'>Messages (participant / coordinator)</Text>
        <Text>Last day: {formatMessages(roll, '1d')}</Text>
        <Text>Last 7 days: {formatMessages(roll, '7d')}</Text>
        <Text>Last 30 days: {formatMessages(roll, '30d')}</Text>
      </Stack>
    </Stack>
  )
}

export default function ParticipantJourneyStatsPanel ({ stats }) {
  const [groupId, setGroupId] = useState('')
  const [siteId, setSiteId] = useState('')

  const groupIds = useMemo(() => Object.keys(stats?.byGroup || {}), [stats])
  const siteIds = useMemo(() => Object.keys(stats?.bySite || {}), [stats])

  const groupRoll = groupId && stats?.byGroup?.[groupId] ? stats.byGroup[groupId] : null
  const siteRoll = siteId && stats?.bySite?.[siteId] ? stats.bySite[siteId] : null

  if (!stats) return null

  const computed = stats.computedAt ? utils.formatDateTime(stats.computedAt) : null

  const groupSelect = groupIds.length > 0 && (
    <Select
      placeholder='Choose group'
      value={groupId}
      onChange={e => setGroupId(e.target.value)}
      maxW='280px'
    >
      {groupIds.map(id => (
        <option key={id} value={id}>{stats.groupLabels?.[id] || id}</option>
      ))}
    </Select>
  )

  const siteSelect = siteIds.length > 0 && (
    <Select
      placeholder='Choose site'
      value={siteId}
      onChange={e => setSiteId(e.target.value)}
      maxW='280px'
    >
      {siteIds.map(id => (
        <option key={id} value={id}>{stats.siteLabels?.[id] || id}</option>
      ))}
    </Select>
  )

  return (
    <Card variant='subtle'>
      <Stack gap={3}>
        <Stack direction='row' justifyContent='space-between' alignItems='flex-start' flexWrap='wrap' gap={2}>
          <Text fontWeight='semibold'>Participant journey</Text>
          {computed && <Text fontSize='xs' color='fg.subtle'>Updated {computed}</Text>}
        </Stack>
        <SimpleTabs
          defaultValue='trial'
          tabs={[
            {
              key: 'trial',
              title: 'Trial-wide',
              content: <RollupDetail roll={stats.trial} />
            },
            {
              key: 'group',
              title: 'By group',
              content: (
                <Stack gap={3}>
                  {groupIds.length === 0 && <Text fontSize='sm' color='fg.subtle'>No groups on participants yet.</Text>}
                  {groupSelect}
                  {groupIds.length > 0 && !groupId && <Text fontSize='sm' color='fg.subtle'>Select a group to view stats.</Text>}
                  {groupId && groupRoll && <RollupDetail roll={groupRoll} />}
                </Stack>
              )
            },
            {
              key: 'site',
              title: 'By site',
              content: (
                <Stack gap={3}>
                  {siteIds.length === 0 && <Text fontSize='sm' color='fg.subtle'>No sites on participants yet.</Text>}
                  {siteSelect}
                  {siteIds.length > 0 && !siteId && <Text fontSize='sm' color='fg.subtle'>Select a site to view stats.</Text>}
                  {siteId && siteRoll && <RollupDetail roll={siteRoll} />}
                </Stack>
              )
            }
          ]}
        />
      </Stack>
    </Card>
  )
}
